import { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import Modal from '../Modal'
import InputField from '../InputField'
import Select from '../Select'
import {  
    Language, 
    CustomerModel,
    UpdateCustomerModel,
    PortalUserAuthenticationScheme,
    BankTransferType
} from '../../redux/types'
import S1 from '../typography/S1'

interface Props {
    initialValue?: CustomerModel
    onSaveClick?: (updateModel: UpdateCustomerModel) => void
    onCancelClick?: () => void
    visible: boolean
}

const EditCustomerModal = ({
    initialValue,
    onSaveClick,
    onCancelClick,
    visible
}: Props) => {
    const styles = useStyles()
    const [updateModel, setUpdateModel] = useState<UpdateCustomerModel>()

    useEffect(() => {
        if (initialValue) {
            setUpdateModel({
                companyRegistrationNumber: initialValue.companyRegistrationNumber,
                name: initialValue.name,
                legalName: initialValue.legalName,
                industry: initialValue.industry,
                agreementNumber: initialValue.agreementNumber,
                language: initialValue.language,
                invoiceEmailAddress: initialValue.invoiceEmailAddress,
                bankName: initialValue.bankName,
                bankAccountType: initialValue.bankAccountType ?? BankTransferType.domesticBbanTransfer,
                bankAccountNumber: initialValue.bankAccountNumber,
                bic: initialValue.bic,
                timeZoneId: initialValue.timeZoneId,
                defaultPortalUserAuthenticationScheme: initialValue.defaultPortalUserAuthenticationScheme
            })
        }
    }, [initialValue])

    const handleOnSave = () => {
        if (updateModel) {
            onSaveClick && onSaveClick(updateModel)
        }
    }

    const handleOnClose = () => {
        onCancelClick && onCancelClick()
    }

    const handleOnChange = (propName: string, value: any) => {
        if (updateModel) {
            setUpdateModel({...updateModel, [propName]: value === '' ? null : value})
        }
    }

    const getLanguageOptions = () => {
        return Object.keys(Language).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getPortalAuthSchemeOptions = () => {
        return Object.keys(PortalUserAuthenticationScheme).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getBankTransferTypeOptions = () => {
        return Object.keys(BankTransferType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    return (
        <Modal
            title='Edit customer'
            show={visible}
            okButtonTitle='Save'
            cancelButtonTitle='Cancel'
            onHide={() => handleOnClose()}
            onOkClick={() => handleOnSave()}
            onCancelClick={() => handleOnClose()}
        >
            <S1 className={styles.sectionTitle}>General</S1>
            <InputField
                id='companyRegistationNumber'
                disabled={true}
                value={updateModel?.companyRegistrationNumber ?? ''}
                label='Company registration number'
                placeholder=''
              />
            <InputField
                id='legalName'
                value={updateModel?.legalName ?? ''}
                onChange={(event) => handleOnChange('legalName', event.target.value)}
                label='Legal name'
                placeholder=''
              />
            <InputField
                id='name'
                value={updateModel?.name ?? ''}
                onChange={(event) => handleOnChange('name', event.target.value)}
                label='Name'
                placeholder=''
              />
            <InputField
                id='agreementNumber'
                value={updateModel?.agreementNumber ?? ''}
                onChange={(event) => handleOnChange('agreementNumber', event.target.value)}
                label='Agreement number'
                placeholder=''
              />
            <InputField
                id='industry'
                value={updateModel?.industry ?? ''}
                onChange={(event) => handleOnChange('industry', event.target.value)}
                label='Industry'
                placeholder=''
              />
            <InputField
                id='invoiceEmailAddress'
                value={updateModel?.invoiceEmailAddress ?? ''}
                onChange={(event) => handleOnChange('invoiceEmailAddress', event.target.value)}
                label='Invoice address'
                placeholder=''
            />
            <InputField
                id='bankName'
                value={updateModel?.bankName ?? ''}
                onChange={(event) => handleOnChange('bankName', event.target.value)}
                label='Bank'
                placeholder=''
            />
            <Select
                id='bankAccountType'
                key='bankAccountType'
                label='Bank account type'
                options={getBankTransferTypeOptions()}
                selectedValue={updateModel?.bankAccountType?.toString()}
                onSelect={(value) => handleOnChange('bankAccountType', value)}
            />
            <InputField
                id='bankAccountNumber'
                value={updateModel?.bankAccountNumber ?? ''}
                onChange={(event) => handleOnChange('bankAccountNumber', event.target.value)}
                label='Bank account'
                placeholder=''
            />
            <InputField
                id='bic'
                value={updateModel?.bic ?? ''}
                onChange={(event) => handleOnChange('bic', event.target.value)}
                label='Bic'
                placeholder=''
            />
            <InputField
                id='timeZoneId'
                disabled={true}
                value={updateModel?.timeZoneId ?? ''}
                label='Time zone'
                placeholder=''
              />
            <Select
                id='language'
                key='language'
                label='Language'
                options={getLanguageOptions()}
                selectedValue={updateModel?.language.toString()}
                onSelect={(value) => handleOnChange('language', Language[value as keyof typeof Language])}
            />
            <Select
                id='defaultPortalUserAuthenticationScheme'
                key='defaultPortalUserAuthenticationScheme'
                label='Portal auth scheme'
                options={getPortalAuthSchemeOptions()}
                selectedValue={updateModel?.defaultPortalUserAuthenticationScheme.toString()}
                onSelect={(value) => handleOnChange('defaultPortalUserAuthenticationScheme', value)}
            />
        </Modal>
    )
}

export default EditCustomerModal

const useStyles = createUseStyles({
    container: {
        alignItems: 'center',
        textAlign: 'center',
        padding: '12px 20px',
        margin: '10px 0',
    },
    sectionTitle: {
        marginBottom: 15
    }
})
